import { useSyncExternalStore } from 'react';
import type { ApiError } from '@command-room/shared';

// Set by any 401 once a session cookie has expired or been revoked. AuthGate
// shows the login overlay while it is true, so the current page (and any
// unsaved edits on it) stays mounted underneath.
let sessionExpired = false;
const expiryListeners = new Set<() => void>();

function setSessionExpired(value: boolean): void {
  if (sessionExpired === value) return;
  sessionExpired = value;
  expiryListeners.forEach((listener) => listener());
}

export function markSessionExpired(): void {
  setSessionExpired(true);
}

export function clearSessionExpired(): void {
  setSessionExpired(false);
}

export function useSessionExpired(): boolean {
  return useSyncExternalStore(
    (listener) => {
      expiryListeners.add(listener);
      return () => expiryListeners.delete(listener);
    },
    () => sessionExpired,
  );
}

export const SESSION_EXPIRED_MESSAGE = 'Session expired. Please sign in again.';

export class ApiRequestError extends Error {
  constructor(
    public status: number,
    public body: ApiError | { error: string; message: string },
  ) {
    super(body.message);
    this.name = 'ApiRequestError';
  }
}

interface RequestOpts extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

async function request<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const { body, headers, ...rest } = opts;
  const init: RequestInit = {
    ...rest,
    credentials: 'include',
    headers: {
      'X-Requested-With': 'command-room',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }

  const res = await fetch(`/api${path}`, init);
  if (!res.ok) {
    let payload: ApiError;
    try {
      payload = (await res.json()) as ApiError;
    } catch {
      payload = { error: res.statusText, message: 'Request failed' };
    }
    // A 401 from /auth/login is a wrong password, not an expired session.
    if (res.status === 401 && path !== '/auth/login') {
      markSessionExpired();
      payload = { ...payload, message: SESSION_EXPIRED_MESSAGE };
    }
    throw new ApiRequestError(res.status, payload);
  }

  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) {
    return (await res.json()) as T;
  }
  return (await res.text()) as unknown as T;
}

export const api = {
  get: <T>(path: string, opts?: RequestOpts) => request<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: RequestOpts) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOpts) =>
    request<T>(path, { ...opts, method: 'PATCH', body }),
  del: <T>(path: string, opts?: RequestOpts) => request<T>(path, { ...opts, method: 'DELETE' }),
};
